/**
 * Email Validation Utility
 * Comprehensive email validation based on RFC 5321 with security enhancements
 */

import apiClient from './axios';

// Validation result types
export interface EmailValidationResult {
  isValid: boolean;
  error: string | null;
  warning: string | null; // For soft warnings (TLD typos)
}

export interface EmailCheckResult {
  exists: boolean;
  error: string | null;
}

// Common TLD typos and their corrections
const TLD_TYPO_MAP: Record<string, string> = {
  'con': 'com',
  'cmo': 'com',
  'ocm': 'com',
  'vom': 'com',
  'xom': 'com',
  'ogr': 'org',
  'orge': 'org',
  'rog': 'org',
  'net': 'net', // correct, no warning
  'nte': 'net',
  'nett': 'net',
  'edu': 'edu', // correct, no warning
  'eud': 'edu',
  'goc': 'gov',
  'goiv': 'gov',
};

// Security characters that are never allowed
const SECURITY_CHARS_REGEX = /[<>,;'"#()[\]\\%&\x00\r\n\u200B-\u200D\uFEFF]/;

// ASCII printable characters only (space to tilde, excluding space)
const NON_ASCII_REGEX = /[^\x21-\x7E@]/;

/**
 * Validate email format according to PRD rules
 * Returns validation result with error or warning message
 */
export function validateEmail(email: string): EmailValidationResult {
  // Auto-trim whitespace
  const trimmed = email.trim();

  // E01: Email is not empty
  if (!trimmed) {
    return { isValid: false, error: 'Email is required', warning: null };
  }

  // E17: Total length >= 5 characters
  if (trimmed.length < 5) {
    return { isValid: false, error: 'Email too short', warning: null };
  }

  // E15: Total length <= 254 characters
  if (trimmed.length > 254) {
    return { isValid: false, error: 'Email too long', warning: null };
  }

  // E07: No whitespace characters
  if (/\s/.test(trimmed)) {
    return { isValid: false, error: 'Spaces not allowed', warning: null };
  }

  // Security rules: No dangerous characters
  if (SECURITY_CHARS_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Invalid characters', warning: null };
  }

  // S14: ASCII only (except for the trimmed email)
  if (NON_ASCII_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Invalid characters', warning: null };
  }

  // E02: Contains exactly one @ symbol
  const atParts = trimmed.split('@');
  if (atParts.length !== 2) {
    return { isValid: false, error: 'Invalid email format', warning: null };
  }

  const [localPart, domain] = atParts;

  // E03: Has local part (before @)
  if (!localPart) {
    return { isValid: false, error: 'Missing username', warning: null };
  }

  // E16: Local part <= 64 characters
  if (localPart.length > 64) {
    return { isValid: false, error: 'Username too long', warning: null };
  }

  // E09: Local part doesn't start with dot
  if (localPart.startsWith('.')) {
    return { isValid: false, error: 'Invalid username format', warning: null };
  }

  // E10: Local part doesn't end with dot
  if (localPart.endsWith('.')) {
    return { isValid: false, error: 'Invalid username format', warning: null };
  }

  // E04: Has domain part (after @)
  if (!domain) {
    return { isValid: false, error: 'Missing domain', warning: null };
  }

  // E13: Domain doesn't start with dot
  if (domain.startsWith('.')) {
    return { isValid: false, error: 'Invalid domain', warning: null };
  }

  // E14: Domain doesn't end with dot
  if (domain.endsWith('.')) {
    return { isValid: false, error: 'Invalid domain', warning: null };
  }

  // E11: Domain doesn't start with hyphen
  if (domain.startsWith('-')) {
    return { isValid: false, error: 'Invalid domain', warning: null };
  }

  // E12: Domain doesn't end with hyphen
  if (domain.endsWith('-')) {
    return { isValid: false, error: 'Invalid domain', warning: null };
  }

  // E05: Domain contains at least one dot
  if (!domain.includes('.')) {
    return { isValid: false, error: 'Invalid domain extension', warning: null };
  }

  // E08: No consecutive dots anywhere
  if (trimmed.includes('..')) {
    return { isValid: false, error: 'Invalid format', warning: null };
  }

  // E18: Domain label max 63 chars
  const domainLabels = domain.split('.');
  for (const label of domainLabels) {
    // Reject IDN/punycode (ACE prefix). Closes the emoji-domain vector: the browser
    // encodes a Unicode/emoji domain to xn--… before this runs, so the ASCII-only
    // rule above can't catch it. Members use standard ASCII corporate emails. (TW-153)
    if (label.toLowerCase().startsWith('xn--')) {
      return { isValid: false, error: 'Internationalized (IDN/emoji) email domains are not allowed', warning: null };
    }
    if (label.length > 63) {
      return { isValid: false, error: 'Invalid domain', warning: null };
    }
    if (label.length === 0) {
      return { isValid: false, error: 'Invalid domain', warning: null };
    }
    // Check each label doesn't start/end with hyphen
    if (label.startsWith('-') || label.endsWith('-')) {
      return { isValid: false, error: 'Invalid domain', warning: null };
    }
  }

  // E06: TLD length between 2 and 24 characters
  const tld = domainLabels[domainLabels.length - 1].toLowerCase();
  if (tld.length < 2 || tld.length > 24) {
    return { isValid: false, error: 'Invalid domain extension', warning: null };
  }

  // Validate local part characters (allow: a-z A-Z 0-9 . _ + -)
  const validLocalPartRegex = /^[a-zA-Z0-9._+-]+$/;
  if (!validLocalPartRegex.test(localPart)) {
    return { isValid: false, error: 'Invalid username format', warning: null };
  }

  // Validate domain characters (allow: a-z A-Z 0-9 - .)
  const validDomainRegex = /^[a-zA-Z0-9.-]+$/;
  if (!validDomainRegex.test(domain)) {
    return { isValid: false, error: 'Invalid domain', warning: null };
  }

  // N08: TLD typo detection (soft warning)
  let warning: string | null = null;
  const suggestedTld = TLD_TYPO_MAP[tld];
  if (suggestedTld && suggestedTld !== tld) {
    warning = `Did you mean .${suggestedTld}?`;
  }

  return { isValid: true, error: null, warning };
}

/**
 * Check if email already exists in the database
 * Call this after format validation passes
 */
export async function checkEmailExists(
  email: string,
  excludeMemberId?: number
): Promise<EmailCheckResult> {
  try {
    const params = new URLSearchParams({
      action: 'check',
      email: email.toLowerCase(),
    });
    if (excludeMemberId) {
      params.append('exclude_member_id', String(excludeMemberId));
    }

    const response = await apiClient.get<{ exists: boolean }>(
      `/email?${params.toString()}`
    );

    if (response.data.exists) {
      return { exists: true, error: 'Email already exists' };
    }

    return { exists: false, error: null };
  } catch (error) {
    // If check fails, return error so user knows validation couldn't complete
    console.error('Email existence check failed:', error);
    return { exists: false, error: 'Unable to verify email. Please try again.' };
  }
}

/**
 * Full email validation including async duplicate check
 */
export async function validateEmailFull(
  email: string,
  excludeMemberId?: number
): Promise<EmailValidationResult & { isDuplicate: boolean }> {
  // First, run format validation
  const formatResult = validateEmail(email);

  if (!formatResult.isValid) {
    return { ...formatResult, isDuplicate: false };
  }

  // Then check for duplicates
  const duplicateResult = await checkEmailExists(email, excludeMemberId);

  if (duplicateResult.exists) {
    return {
      isValid: false,
      error: duplicateResult.error,
      warning: formatResult.warning,
      isDuplicate: true,
    };
  }

  return { ...formatResult, isDuplicate: false };
}

/**
 * Normalize email for storage (lowercase)
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Get suggested correction for TLD typo
 */
export function getSuggestedEmail(email: string): string | null {
  const trimmed = email.trim();
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex === -1) return null;

  const domain = trimmed.substring(atIndex + 1);
  const lastDotIndex = domain.lastIndexOf('.');
  if (lastDotIndex === -1) return null;

  const tld = domain.substring(lastDotIndex + 1).toLowerCase();
  const suggestedTld = TLD_TYPO_MAP[tld];

  if (suggestedTld && suggestedTld !== tld) {
    const baseDomain = domain.substring(0, lastDotIndex + 1);
    return trimmed.substring(0, atIndex + 1) + baseDomain + suggestedTld;
  }

  return null;
}
